/**
 * Client + server error reporting.
 * On the client, batches and posts errors to the `reportClientError` server fn
 * (which stores them in `public.client_errors`). Source maps are emitted in the
 * production build so stack traces symbolicate in browser devtools and, once
 * fed through a source-map consumer, in stored reports too.
 */
import { reportClientError } from "@/lib/errors/report.functions";

type ReportContext = Record<string, unknown> | undefined;

const RELEASE = (import.meta.env.VITE_APP_RELEASE as string | undefined) ?? "dev";
const DEDUPE_WINDOW_MS = 5_000;
const recent = new Map<string, number>();

function fingerprint(message: string, stack?: string | null) {
  return `${message}::${(stack ?? "").slice(0, 200)}`;
}

function shouldSkip(message: string, stack?: string | null) {
  const key = fingerprint(message, stack);
  const now = Date.now();
  const last = recent.get(key);
  if (last && now - last < DEDUPE_WINDOW_MS) return true;
  recent.set(key, now);
  if (recent.size > 100) {
    const cutoff = now - DEDUPE_WINDOW_MS * 4;
    for (const [k, t] of recent) if (t < cutoff) recent.delete(k);
  }
  return false;
}

export function reportLovableError(error: unknown, context?: ReportContext & { boundary?: string }) {
  try {
    const err = normalizeError(error);
    if (shouldSkip(err.message, err.stack)) return;

    const isBrowser = typeof window !== "undefined";
    void reportClientError({
      data: {
        message: err.message.slice(0, 4000),
        stack: err.stack ? err.stack.slice(0, 20000) : null,
        boundary: context?.boundary ?? null,
        url: isBrowser ? window.location.href.slice(0, 2000) : null,
        userAgent: isBrowser ? navigator.userAgent.slice(0, 1000) : null,
        release: RELEASE,
        side: isBrowser ? "client" : "server",
        context: context ? sanitizeContext(context) : null,
      },
    }).catch(() => {
      /* swallow — never let reporting cause more errors */
    });
  } catch {
    /* noop */
  }
}

function normalizeError(error: unknown): { message: string; stack?: string } {
  if (error instanceof Error) return { message: error.message || error.name, stack: error.stack };
  if (typeof error === "string") return { message: error };
  try {
    return { message: JSON.stringify(error) };
  } catch {
    return { message: String(error) };
  }
}

function sanitizeContext(ctx: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(ctx)) {
    if (v === undefined) continue;
    if (typeof v === "function") continue;
    out[k] = v;
  }
  return out;
}

let installed = false;
export function installBrowserErrorReporting() {
  if (installed) return;
  if (typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    reportLovableError(event.error ?? event.message, {
      boundary: "window.onerror",
      filename: (event as ErrorEvent).filename,
      lineno: (event as ErrorEvent).lineno,
      colno: (event as ErrorEvent).colno,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    reportLovableError((event as PromiseRejectionEvent).reason, {
      boundary: "unhandledrejection",
    });
  });
}